import { useSimulatedBilling } from '@/hooks/use-billing-simulator.hook';
import { fetchWebtokenHeadless } from '@/store/auth-helper';
import { useGetPlansQuery } from '@/store/features/billing.feature';
import type { AvailablePlans } from '@/types';
import { useOrganization, useUser } from '@clerk/react';
import Intercom from '@intercom/messenger-js-sdk';
import { useEffect, useRef, useState } from 'react';

const INTERCOM_APP_ID = 'nk8jyhrv';

/**
 * Plan attributes mirrored into Intercom so routing rules and Fin can key off
 * the org's billing posture without leaking raw tier ints. Set as both custom
 * user and custom company attributes (AppSumo redemption is org-scoped).
 *
 * `appsumo_partner_plan` carries the AppSumo tier's own label ("AppSumo Tier
 * 5"): each tier is its own plan, never Starter or Pro, and the API sends the
 * same value on the company (cleanse-api intercom_provider).
 */
interface IntercomPlanAttrs {
  is_appsumo: boolean;
  appsumo_tier: number | null;
  appsumo_partner_plan: string | null;
  plan_type: 'appsumo' | 'stripe' | 'free';
}

/**
 * Derives Intercom plan attributes from the GET /billing/plans response.
 * Returns null until the query resolves, so callers can skip pushing
 * placeholder values before the real plan is known.
 */
function buildPlanAttrs(
  plans: AvailablePlans | undefined
): IntercomPlanAttrs | null {
  if (!plans) return null;
  return {
    is_appsumo: plans.appsumo !== null,
    appsumo_tier: plans.appsumo?.tier ?? null,
    appsumo_partner_plan: plans.appsumo?.label ?? null,
    plan_type: plans.appsumo
      ? 'appsumo'
      : plans.current_plan
        ? 'stripe'
        : 'free',
  };
}

const apiBase = (
  import.meta.env.VITE_API_URL || 'http://localhost:8000'
).replace(/\/$/, '');
const baseApiUrl = apiBase.endsWith('/api/v1') ? apiBase : `${apiBase}/api/v1`;

async function fetchIntercomHash(): Promise<string | null> {
  try {
    const token = await fetchWebtokenHeadless();
    if (!token) return null;
    const res = await fetch(`${baseApiUrl}/intercom/identity-hash`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.user_hash ?? null;
  } catch {
    return null;
  }
}

/**
 * Boots Intercom when a Clerk user is available, shuts down on sign-out.
 * Fetches the identity verification hash from the backend for secure mode.
 * Call once in a persistent shell component (e.g. AppShell).
 */
export function useIntercomBoot() {
  const { user, isSignedIn } = useUser();
  const { organization } = useOrganization();
  // Mirror billing posture into Intercom. RTK Query dedupes this against the
  // plans/billing pages, so it's effectively free here.
  const { currentData: plans } = useGetPlansQuery(
    { organizationId: organization?.id ?? '' },
    { skip: !isSignedIn || !organization?.id }
  );
  // A dev billing scenario's plan stays out of Intercom: it would land on the
  // real organization's record. Never true in production.
  const simulatedBilling = useSimulatedBilling();
  const bootedRef = useRef(false);

  useEffect(() => {
    if (!isSignedIn || !user || bootedRef.current) return;

    bootedRef.current = true;

    // May be null if /billing/plans hasn't resolved yet at boot — the
    // reactive effect below backfills the attributes once it does.
    const planAttrs = simulatedBilling ? null : buildPlanAttrs(plans);

    // Boot immediately without hash, then upgrade with identity verification
    Intercom({
      app_id: INTERCOM_APP_ID,
      user_id: user.id,
      name: user.fullName ?? undefined,
      email: user.primaryEmailAddress?.emailAddress ?? undefined,
      created_at: user.createdAt
        ? Math.floor(new Date(user.createdAt).getTime() / 1000)
        : undefined,
      hide_default_launcher: true,
      company: organization
        ? {
            company_id: organization.id,
            name: organization.name,
            ...(planAttrs ?? {}),
          }
        : undefined,
      completed_onboarding: !!user.unsafeMetadata?.completed_onboarding,
      organization_name: organization?.name ?? undefined,
      ...(planAttrs ?? {}),
    });

    fetchIntercomHash().then((hash) => {
      if (hash) {
        window.Intercom?.('update', { user_hash: hash });
      }
    });
  }, [isSignedIn, user, organization, plans, simulatedBilling]);

  // Reactive plan-attribute sync: pushes is_appsumo/appsumo_tier/etc. whenever
  // the plans query resolves or refetches (e.g. after a code stack, upgrade, or
  // refund webhook invalidates the Plans tag) — so an org's billing posture in
  // Intercom isn't stale until the next page reload.
  useEffect(() => {
    if (!bootedRef.current || simulatedBilling) return;
    const planAttrs = buildPlanAttrs(plans);
    if (!planAttrs) return;
    window.Intercom?.('update', {
      ...planAttrs,
      company: organization
        ? { company_id: organization.id, ...planAttrs }
        : undefined,
    });
  }, [plans, organization, simulatedBilling]);

  // AppSumo: hide the floating Intercom launcher (the round bubble
  // bottom-right). AppSumo orgs have the AI Copilot disabled and the bubble
  // reads as a Copilot/AI launcher, so we suppress it via a CSS rule keyed off
  // <html data-appsumo>. Support stays reachable through the sidebar "Live
  // Chat" button (Intercom('show')). The flag clears on sign-out / non-AppSumo
  // so the launcher returns for everyone else.
  useEffect(() => {
    const root = document.documentElement;
    const isAppSumo = isSignedIn && plans?.appsumo != null;
    if (isAppSumo) {
      root.setAttribute('data-appsumo', 'true');
    } else {
      root.removeAttribute('data-appsumo');
    }
    return () => {
      root.removeAttribute('data-appsumo');
    };
  }, [isSignedIn, plans]);

  useEffect(() => {
    if (!isSignedIn && bootedRef.current) {
      bootedRef.current = false;
      window.Intercom?.('shutdown');
    }
  }, [isSignedIn]);
}

/**
 * Call on route changes to let Intercom track page views and
 * check for new messages/tours. Rate-limited by Intercom to 20/30min.
 */
export function pingIntercom() {
  window.Intercom?.('update');
}

/**
 * Maps the current pathname to a stable `page_key` enum.
 * Used by Intercom Outbound Chats to target proactive Fin greetings
 * against a custom attribute instead of fragile URL globs.
 *
 * Returns `null` for pages that should not trigger proactive greetings
 * (admin, auth, error pages).
 */
export function getPageKey(pathname: string): string | null {
  const p = pathname.replace(/\/+$/, '') || '/';

  if (p === '/') return 'dashboard';
  if (p === '/finder') return 'search';
  if (p === '/agents') return 'smart_agents';
  if (p === '/integrations') return 'integrations';
  if (p === '/api-keys') return 'api_keys';
  if (p === '/reports') return 'analytics';
  if (p === '/plans') return 'billing';
  if (p === '/onboarding') return 'onboarding';
  if (p === '/settings/notifications') return 'notification_settings';
  if (p === '/extension' || p === '/extension-feedback') return 'extension';

  if (p === '/lists') return 'lead_lists';
  if (p.startsWith('/lists/')) return 'lead_list_detail';

  if (p === '/people') return 'people_directory';
  if (p.startsWith('/people/')) return 'person_profile';

  if (p === '/companies') return 'companies_directory';
  if (p.startsWith('/companies/')) return 'company_profile';

  return null;
}

/**
 * Update user attributes in Intercom (credits, plan, etc.).
 * Call after data changes that should be reflected in Intercom.
 */
export function updateIntercomUser(attrs: Record<string, unknown>) {
  window.Intercom?.('update', attrs);
}

/**
 * Track a custom event in Intercom for analytics and Fin context.
 * @param name - past-tense event name (e.g. "created-list", "ran-enrichment")
 * @param metadata - up to 20 key-value pairs
 */
export function trackIntercomEvent(
  name: string,
  metadata?: Record<string, string | number | boolean>
) {
  window.Intercom?.('trackEvent', name, metadata);
}

/** Programmatically show the Intercom Messenger. */
export function showIntercom() {
  window.Intercom?.('show');
}

/**
 * Subscribe to Intercom's unread message count.
 * Returns the current count, updated in real-time.
 */
export function useIntercomUnreadCount(): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!window.Intercom) return;
    window.Intercom('onUnreadCountChange', (n: number) => {
      setCount(n);
    });
  }, []);

  return count;
}
